import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ContextoApi, useApi, useConsulta } from './api';
import { fechaCorta } from './formato';
import { useSesion } from './sesion';

/**
 * Bitácora de errores en los frentes (arquitectura 2.27): cada frente reporta sus excepciones de
 * JavaScript y las llamadas al API que no llegaron (sin red o el Gateway caído) a
 * `POST /platform/errores`; el lotero sale del token. Los 5xx ya los registra cada servicio.
 */
export type Frente = 'admin' | 'superadmin' | 'soporte' | 'operacion' | 'portal';

/** `useApi` avisa por aquí cuando una llamada no llegó al servidor. */
export const EVENTO_ERROR_RED = 'plataforma:error-red';
const MAXIMO_POR_PAGINA = 20;
const REPETIDO_MS = 60_000;

/** Instala el reporte una vez por frente (dentro de la sesión). Nunca lanza ni insiste. */
export function useReporteErrores(frente: Frente) {
  const base = useContext(ContextoApi);
  const { usuario, token } = useSesion();
  useEffect(() => {
    if (!usuario) return;
    let enviados = 0;
    const vistos = new Map<string, number>();
    const enviar = async (codigo: string, mensaje: string) => {
      const pantalla = window.location.pathname;
      const llave = `${codigo}|${mensaje}|${pantalla}`;
      const ahora = Date.now();
      if (enviados >= MAXIMO_POR_PAGINA || ahora - (vistos.get(llave) ?? 0) < REPETIDO_MS) return;
      vistos.set(llave, ahora);
      enviados++;
      try {
        await fetch(`${base}/api/platform/errores`, {
          method: 'POST',
          headers: {
            authorization: `Bearer ${await token()}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            frente,
            codigo: codigo.slice(0, 80) || 'Error',
            mensaje: mensaje.slice(0, 2000) || 'Error sin mensaje',
            pantalla: pantalla.slice(0, 300),
          }),
          keepalive: true,
        });
      } catch {
        // Si no se puede reportar, no hay nada más que hacer.
      }
    };
    const alError = (e: ErrorEvent) => {
      const error = e.error as Error | undefined;
      void enviar(error?.name ?? 'Error', error?.message ?? e.message);
    };
    const alRechazo = (e: PromiseRejectionEvent) => {
      const r = e.reason as { name?: string; message?: string } | undefined;
      // Los errores del API ya los registró el servidor (5xx) o son de validación (4xx).
      if (r && 'estado' in r) return;
      void enviar(r?.name ?? 'PromesaRechazada', r?.message ?? String(e.reason));
    };
    const alFallarRed = (e: Event) => {
      const d = (e as CustomEvent<{ ruta: string; mensaje: string }>).detail;
      void enviar('SinRespuesta', `${d.ruta}: ${d.mensaje}`);
    };
    window.addEventListener('error', alError);
    window.addEventListener('unhandledrejection', alRechazo);
    window.addEventListener(EVENTO_ERROR_RED, alFallarRed);
    return () => {
      window.removeEventListener('error', alError);
      window.removeEventListener('unhandledrejection', alRechazo);
      window.removeEventListener(EVENTO_ERROR_RED, alFallarRed);
    };
  }, [base, frente, usuario, token]);
}

type EstadoError = 'nuevo' | 'en_revision' | 'resuelto' | 'ignorado';
const ESTADOS: [EstadoError, string][] = [
  ['nuevo', 'Nuevo'],
  ['en_revision', 'En revisión'],
  ['resuelto', 'Resuelto'],
  ['ignorado', 'Ignorado'],
];
const NOMBRE_ESTADO = Object.fromEntries(ESTADOS) as Record<EstadoError, string>;
const ORIGEN: Record<string, string> = {
  servidor: 'Servidor',
  integracion: 'Integración',
  trabajo: 'Proceso automático',
  navegador: 'Navegador',
};

interface ErrorEquipo {
  id: string;
  tenant_id: string;
  negocio: string | null;
  servicio: string;
  origen: string;
  codigo: string;
  mensaje: string;
  lugar: string;
  conteo: number;
  primera_vez: string;
  ultima_vez: string;
  estado: EstadoError;
  ultima_peticion: string | null;
  ticket_id: string | null;
}

/**
 * Bitácora para los paneles del equipo (Super Admin, Soporte y Operación): detalle técnico
 * saneado; quien puede, cambia el estado. `ruta` es la del Gateway (p. ej. `/platform/soporte/errores`).
 */
/** Enlace a los logs de la petición en Grafana (Loki), solo para Super Admin (2.27). */
export function enlaceGrafana(
  grafana: string,
  e: { servicio: string; ultima_peticion: string | null },
) {
  const filtro = e.ultima_peticion ? ` | peticion=\`${e.ultima_peticion}\`` : '';
  const izquierda = {
    datasource: 'loki',
    queries: [{ refId: 'A', expr: `{servicio="${e.servicio}"}${filtro}` }],
    range: { from: 'now-7d', to: 'now' },
  };
  return `${grafana}/explore?left=${encodeURIComponent(JSON.stringify(izquierda))}`;
}

export function BitacoraErrores({
  ruta,
  editable,
  grafana,
}: {
  ruta: string;
  editable: boolean;
  /** URL de Grafana del NOC: solo Super Admin enlaza a los logs técnicos. */
  grafana?: string;
}) {
  const api = useApi();
  const [estado, setEstado] = useState<EstadoError | ''>('nuevo');
  const [origen, setOrigen] = useState('');
  const [dias, setDias] = useState('7');
  const q = new URLSearchParams(
    Object.entries({ estado, origen, dias }).filter(([, v]) => v),
  ).toString();
  const { datos, error, recargar } = useConsulta<ErrorEquipo[]>(`${ruta}?${q}`);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const cambiar = async (id: string, nuevo: EstadoError) => {
    setAviso(null);
    try {
      await api('PATCH', `${ruta}/${id}`, { estado: nuevo });
      recargar();
    } catch (e) {
      setAviso((e as Error).message);
    }
  };
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Errores</h1>
          <div className="subtitulo">
            Agrupados por huella: cada fila es un error distinto con cuántas veces pasó
          </div>
        </div>
      </div>
      <section className="seccion">
        <header>
          <select
            aria-label="Estado"
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoError | '')}
          >
            <option value="">Todos los estados</option>
            {ESTADOS.map(([c, n]) => (
              <option key={c} value={c}>
                {n}
              </option>
            ))}
          </select>
          <select aria-label="Origen" value={origen} onChange={(e) => setOrigen(e.target.value)}>
            <option value="">Todos los orígenes</option>
            {Object.entries(ORIGEN).map(([c, n]) => (
              <option key={c} value={c}>
                {n}
              </option>
            ))}
          </select>
          <select aria-label="Periodo" value={dias} onChange={(e) => setDias(e.target.value)}>
            <option value="1">Último día</option>
            <option value="7">Últimos 7 días</option>
            <option value="30">Últimos 30 días</option>
            <option value="90">Últimos 90 días</option>
          </select>
        </header>
        {error && <p className="error">{error}</p>}
        {aviso && <p className="error">{aviso}</p>}
        {datos && !datos.length && <p className="vacio">Sin errores con estos filtros.</p>}
        {datos && datos.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Error</th>
                <th>Negocio</th>
                <th>Veces</th>
                <th>Última vez</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {datos.map((e) => (
                <tr key={e.id}>
                  <td className="celda-texto">
                    <button
                      type="button"
                      className="enlace"
                      onClick={() => setAbierto(abierto === e.id ? null : e.id)}
                    >
                      {e.codigo} · {e.lugar}
                    </button>
                    <div className="vin">
                      {e.servicio} · {ORIGEN[e.origen] ?? e.origen}
                    </div>
                    {abierto === e.id && (
                      <div className="vin" style={{ whiteSpace: 'pre-wrap' }}>
                        {e.mensaje}
                        {e.ultima_peticion && `\nPetición: ${e.ultima_peticion}`}
                        {`\nPrimera vez: ${fechaCorta(e.primera_vez)} · folio ${e.id.slice(-8).toUpperCase()}`}
                        {grafana && e.servicio.endsWith('-svc') && (
                          <>
                            {' · '}
                            <a href={enlaceGrafana(grafana, e)} target="_blank" rel="noreferrer">
                              Ver logs en Grafana
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td>{e.negocio ?? 'Plataforma'}</td>
                  <td>{e.conteo}</td>
                  <td>{fechaCorta(e.ultima_vez)}</td>
                  <td>
                    {editable ? (
                      <select
                        aria-label={`Estado de ${e.codigo}`}
                        value={e.estado}
                        onChange={(x) => void cambiar(e.id, x.target.value as EstadoError)}
                      >
                        {ESTADOS.map(([c, n]) => (
                          <option key={c} value={c}>
                            {n}
                          </option>
                        ))}
                      </select>
                    ) : (
                      NOMBRE_ESTADO[e.estado]
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

interface ErrorNegocio {
  id: string;
  folio: string;
  titulo: string;
  queHacer: string;
  veces: number;
  ultimaVez: string;
  estado: EstadoError;
}

/** Admin Lotero y Portal (dueño y gerente): solo lo de su negocio, en lenguaje llano. */
export function MisErrores({ ticketNuevo }: { ticketNuevo: string | null }) {
  const { datos, error } = useConsulta<ErrorNegocio[]>('/platform/mi-lotero/errores');
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Errores</h1>
          <div className="subtitulo">
            Lo que falló en tu negocio en los últimos 90 días. Soporte ya lo ve; si te impide
            trabajar, abre un ticket con el folio.
          </div>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {datos && !datos.length && (
        <section className="seccion">
          <p className="vacio">Sin errores. Todo ha funcionado.</p>
        </section>
      )}
      {datos?.map((e) => (
        <section className="seccion" key={e.id}>
          <header>
            <h2>{e.titulo}</h2>
            <div className="relleno" />
            <span className={`etiqueta ${e.estado === 'resuelto' ? 'exito' : 'aviso'}`}>
              {e.estado === 'resuelto'
                ? 'Resuelto'
                : e.estado === 'en_revision'
                  ? 'En revisión'
                  : 'Registrado'}
            </span>
          </header>
          <div className="cuerpo">
            <p>{e.queHacer}</p>
            <p className="subtitulo">
              Folio {e.folio} · {e.veces === 1 ? 'una vez' : `${e.veces} veces`} · última vez{' '}
              {fechaCorta(e.ultimaVez)}
            </p>
            {ticketNuevo && e.estado !== 'resuelto' && (
              <Link
                className="boton"
                to={`${ticketNuevo}?${new URLSearchParams({ asunto: e.titulo.slice(0, 160), referencia: `Error ${e.folio}` })}`}
              >
                Abrir un ticket con este folio
              </Link>
            )}
          </div>
        </section>
      ))}
    </>
  );
}
